import { useEffect, useState } from 'react';
import { ApiError, PUBLIC_CONFIRMATION_REQUIRED, PUBLIC_TREE_RESTRICTED } from '@vwt/contracts';
import { Button, Dialog, DialogContent, DialogFooter } from '@vwt/ui';
import { Globe } from 'lucide-react';
import { useTranslation } from '../../lib/i18n.js';

/**
 * Confirmation before a change makes documents public (ADR-0056). The server refuses a move into the
 * "Public external" folder, an unlock or a restore there with `public_confirmation_required` until the
 * person confirms; `withPublicConfirm` asks and retries the same change with `confirmPublic: true`.
 */
type Pending = { resolve: (confirmed: boolean) => void; message: string };
let show: ((pending: Pending) => void) | null = null;

export function needsPublicConfirmation(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== 'GUARD_VIOLATION') return null;
  const violation = error.envelope?.error.details?.violations?.find((v) => v.code === PUBLIC_CONFIRMATION_REQUIRED);
  return violation ? violation.message : null;
}

/** Whether the server refused a folder change because a restricted folder would sit in the public tree (ADR-0056). */
export function isPublicTreeRestricted(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_FAILED') return false;
  return (error.envelope?.error.details?.fields ?? []).some((f) => f.code === PUBLIC_TREE_RESTRICTED);
}

/** Runs `change`; when the server asks for confirmation, shows the dialog and retries once confirmed. Null when the person cancels. */
export async function withPublicConfirm<T>(change: (confirmPublic: boolean) => Promise<T>): Promise<T | null> {
  try {
    return await change(false);
  } catch (error) {
    const message = needsPublicConfirmation(error);
    if (message === null || !show) throw error;
    const open = show;
    const confirmed = await new Promise<boolean>((resolve) => open({ resolve, message }));
    if (!confirmed) return null;
    return change(true);
  }
}

/** Mounted once in the documents layout. */
export function PublicConfirmHost() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  useEffect(() => {
    show = (next) => setPending(next);
    return () => {
      show = null;
    };
  }, []);
  const close = (confirmed: boolean) => {
    pending?.resolve(confirmed);
    setPending(null);
  };
  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && close(false)}>
      <DialogContent title={t('documents.public.confirmTitle')} description={t('documents.public.confirmBody')}>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => close(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="primary" onClick={() => close(true)}>
            <Globe /> {t('documents.public.confirmAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
